import React, { useState, useEffect, Suspense, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { createReactBlockSpec } from "@blocknote/react";
import { FaCog, FaTrash } from "react-icons/fa";
import { useTextWidgetFilters } from "./text-widget-context";
import { ResizeHandle } from "./resize-handle";
import {
  ContextMenuItem,
  ContextMenuDivider,
} from "@/components/layout/arc-sidebar/context-menu-helpers";
import { Z_INDEX } from "@/utils/z-index";
import { backendWidgetToWidgetV2, widgetV2ToBackendWidget } from "./adapter";
import type { Widget, BackendWidget } from "@/models/widget-v2";
import { WidgetStepperModalV2 } from "./WidgetStepperModalV2";

// Import lazy du WidgetRendererWrapper pour éviter les problèmes de rendu
const TemplateWidgetRenderer = React.lazy(
  () => import("./WidgetRendererWrapper"),
);

// Composant de fallback pendant le chargement
const WidgetLoadingFallback: React.FC<{ height: number }> = ({ height }) => (
  <div
    style={{
      height,
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--arc-surface-secondary)",
      borderRadius: "8px",
    }}
  >
    <span style={{ color: "var(--arc-content-tertiary)" }}>
      Chargement du widget...
    </span>
  </div>
);

// Composant wrapper qui gère le cycle de vie du widget
const WidgetBlockContent: React.FC<{
  widgetType: string;
  widgetName: string;
  widgetConfig: string;
  height: number;
}> = ({ widgetType, widgetName, widgetConfig, height }) => {
  const filtersCtx = useTextWidgetFilters();
  const [isMounted, setIsMounted] = useState(false);

  // Parser la configuration JSON
  let parsedConfig = {};
  try {
    parsedConfig = JSON.parse(widgetConfig);
  } catch {
    console.warn("Failed to parse widget config:", widgetConfig);
  }

  // Attendre que le composant soit monté avant de rendre le widget
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => {
      clearTimeout(timer);
      setIsMounted(false);
    };
  }, []);

  const computedHeight = typeof height === "number" ? height : 200;

  if (!isMounted) {
    return <WidgetLoadingFallback height={computedHeight} />;
  }

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <Suspense fallback={<WidgetLoadingFallback height={computedHeight} />}>
        <TemplateWidgetRenderer
          widgetType={widgetType}
          widgetName={widgetName}
          widgetConfig={parsedConfig}
          filtersCtx={filtersCtx}
        />
      </Suspense>
    </div>
  );
};

/**
 * Inner component for the widget block that manages context menu + configure modal.
 * Extracted so we can use hooks (useState, useEffect, useRef, useCallback).
 */
const WidgetBlockInner: React.FC<{
  block: any;
  editor: any;
}> = ({ block, editor }) => {
  const { widgetType, widgetName, widgetConfig, height } = block.props;
  const computedHeight = typeof height === "number" ? height : 200;
  const filtersCtx = useTextWidgetFilters();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Configure modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null);

  const handleResize = useCallback(
    (newHeight: number) => {
      editor.updateBlock(block, {
        props: { ...block.props, height: newHeight },
      });
    },
    [editor, block],
  );

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 200);
    setContextMenu({ x, y });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleConfigure = useCallback(() => {
    closeContextMenu();
    try {
      const parsed = JSON.parse(widgetConfig) as BackendWidget;
      const widget = backendWidgetToWidgetV2(parsed);
      setEditingWidget(widget);
      setShowConfigModal(true);
    } catch {
      console.warn("Failed to parse widget config for configure:", widgetConfig);
    }
  }, [widgetConfig, closeContextMenu]);

  const handleUpdateWidget = useCallback(
    (updatedWidget: Widget) => {
      const backendWidget = widgetV2ToBackendWidget(updatedWidget);
      editor.updateBlock(block, {
        props: {
          ...block.props,
          widgetType: backendWidget.type || backendWidget.chart || widgetType,
          widgetName: backendWidget.name || widgetName,
          widgetConfig: JSON.stringify(backendWidget),
        },
      });
      setShowConfigModal(false);
      setEditingWidget(null);
    },
    [editor, block, widgetType, widgetName],
  );

  const handleDelete = useCallback(() => {
    closeContextMenu();
    editor.removeBlocks([block]);
  }, [editor, block, closeContextMenu]);

  // Close context menu on outside click / Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeContextMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [contextMenu, closeContextMenu]);

  return (
    <>
      <div
        contentEditable={false}
        className="group"
        onContextMenu={handleContextMenu}
        style={{
          position: "relative",
          height: computedHeight,
          width: "100%",
          borderRadius: "8px",
          overflow: "visible",
          border: "1px solid var(--arc-border)",
          backgroundColor: "var(--arc-surface-elevated)",
        }}
      >
        <div
          style={{
            height: "100%",
            width: "100%",
            position: "relative",
            overflow: "hidden",
            borderRadius: "8px",
          }}
        >
          <WidgetBlockContent
            widgetType={widgetType}
            widgetName={widgetName}
            widgetConfig={widgetConfig}
            height={computedHeight}
          />
        </div>
        <ResizeHandle
          currentHeight={computedHeight}
          minHeight={100}
          maxHeight={1200}
          onResize={handleResize}
        />
      </div>

      {/* Context menu */}
      {contextMenu &&
        createPortal(
          <div
            ref={contextMenuRef}
            className="fixed bg-surface-elevated rounded-arc-md shadow-arc-xl border border-arc-border py-1 min-w-[180px] animate-arc-fade-in"
            style={{
              top: contextMenu.y,
              left: contextMenu.x,
              zIndex: Z_INDEX.POPOVER_IN_MODAL,
            }}
          >
            <ContextMenuItem onClick={handleConfigure}>
              <FaCog className="text-accent" /> Configurer
            </ContextMenuItem>
            <ContextMenuDivider />
            <ContextMenuItem onClick={handleDelete} danger>
              <FaTrash /> Supprimer
            </ContextMenuItem>
          </div>,
          document.body,
        )}

      {/* Configure modal */}
      {showConfigModal && (
        <WidgetStepperModalV2
          open={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setEditingWidget(null);
          }}
          onAddWidget={handleUpdateWidget}
          onUpdateWidget={handleUpdateWidget}
          editingWidget={editingWidget}
          filtersCtx={filtersCtx}
        />
      )}
    </>
  );
};

/**
 * Bloc personnalisé BlockNote pour afficher des widgets inline dans l'éditeur de texte
 */
export const WidgetBlock = createReactBlockSpec(
  {
    type: "widget",
    propSchema: {
      widgetType: {
        default: "kpi_widget",
      },
      widgetName: {
        default: "Widget",
      },
      widgetConfig: {
        default: "{}",
      },
      height: {
        default: 400,
      },
    },
    content: "none",
  },
  {
    render: (props) => {
      return <WidgetBlockInner block={props.block} editor={props.editor} />;
    },
  },
);
